import { z } from 'zod'
import { type TranslationKey } from '#app/locales/uk.ts'

/**
 * «Звідки про нас дізнались?» — опційне поле анкети для відстеження конверсії з
 * соцмереж (team-confirmed батч 1 питання 13 + батч 2 А4; значення дані
 * кураторкою вербатим). Окремий domain-SSOT (не funnel.ts): це конверсійний
 * атрибут подачі, не стан воронки — власна сутність із власним циклом.
 *
 * Зберігається як `Application.sources String @default("")` — comma-joined
 * ключі enum (EN slug). Порожній рядок = «не вказано» (поле опційне).
 */
export const applicationSources = [
	'telegram',
	'facebook',
	'instagram',
	'tiktok',
	'youtube',
	'friend_referral',
] as const
export const ApplicationSourceSchema = z.enum(applicationSources)
export type ApplicationSource = (typeof applicationSources)[number]

/**
 * Схема поля форми: опційний масив відомих ключів. Невалідне значення при
 * ЗАПИСІ з форми → zod-fail → 400 (AC-4). Відсутність вибору → undefined
 * (поле опційне — сабміт без жодного джерела проходить).
 */
export const ApplicationSourcesFieldSchema = z
	.array(ApplicationSourceSchema)
	.optional()

export const applicationSourceLabels: Record<
	ApplicationSource,
	TranslationKey
> = {
	telegram: 'source.telegram',
	facebook: 'source.facebook',
	instagram: 'source.instagram',
	tiktok: 'source.tiktok',
	youtube: 'source.youtube',
	friend_referral: 'source.friend_referral',
}

/** Серіалізація для запису: унікальні ключі, comma-joined. */
export function serializeSources(
	sources: ReadonlyArray<ApplicationSource>,
): string {
	return Array.from(new Set(sources)).join(',')
}

/**
 * Парсинг збереженого рядка. ТОЛЕРАНТНИЙ до невідомих при ЧИТАННІ — фільтрує
 * лише відомі ключі (застарілий/викочений enum-ключ у старому рядку не ламає
 * показ адміну). Дедуп + порожні сегменти відкидаються.
 */
export function parseSources(raw: string): Array<ApplicationSource> {
	const known = new Set<string>(applicationSources)
	return Array.from(
		new Set(
			raw
				.split(',')
				.map((segment) => segment.trim())
				.filter((segment): segment is ApplicationSource => known.has(segment)),
		),
	)
}
